import { useEffect, useRef, useState } from 'react';
import type { CustomerRegisterItemStatus, BlockedReason } from '@aigovops/core';
import { Button, StatusBadge } from '@aigovops/design-system';
import { statusLabel } from '../../lib/format';

const NA_REASONS = [
  { value: 'geographic', label: 'Geographic — jurisdiction does not apply' },
  { value: 'ai_class', label: 'AI classification — system type not in scope' },
  { value: 'superseded', label: 'Superseded by another control' },
  { value: 'risk_accepted', label: 'Risk accepted with rationale' },
  { value: 'other', label: 'Other' },
] as const;

const BLOCKED_REASONS: { value: BlockedReason; label: string }[] = [
  { value: 'waiting_on_lead', label: 'Waiting on lead' },
  { value: 'waiting_on_peer', label: 'Waiting on a peer' },
  { value: 'waiting_on_external', label: 'Waiting on external party' },
  { value: 'scope_pending', label: 'Scope decision pending' },
  { value: 'data_source_unavailable', label: 'Data source unavailable' },
];

interface StatusChangeDialogProps {
  open: boolean;
  currentStatus: CustomerRegisterItemStatus;
  itemTitle: string;
  onConfirm: (status: CustomerRegisterItemStatus, opts?: {
    blocked_reason?: string;
    na_rationale?: string;
    notes?: string;
  }) => void;
  onCancel: () => void;
  saving?: boolean;
}

export function StatusChangeDialog({
  open,
  currentStatus,
  itemTitle,
  onConfirm,
  onCancel,
  saving,
}: StatusChangeDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [selectedStatus, setSelectedStatus] = useState<CustomerRegisterItemStatus>(currentStatus);
  const [blockedReason, setBlockedReason] = useState<BlockedReason>('waiting_on_lead');
  const [naReasonCategory, setNaReasonCategory] = useState('geographic');
  const [naRationale, setNaRationale] = useState('');

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (open) {
      setSelectedStatus(currentStatus);
      setNaRationale('');
    }
  }, [open, currentStatus]);

  const needsRationale = selectedStatus === 'not_applicable' &&
    ['superseded', 'risk_accepted', 'other'].includes(naReasonCategory);

  const canConfirm =
    selectedStatus !== currentStatus &&
    !(selectedStatus === 'blocked' && !blockedReason) &&
    !(selectedStatus === 'not_applicable' && needsRationale && !naRationale.trim());

  function handleConfirm() {
    const opts: Parameters<typeof onConfirm>[1] = {};
    if (selectedStatus === 'blocked') opts.blocked_reason = blockedReason;
    if (selectedStatus === 'not_applicable') {
      opts.na_rationale = naRationale.trim() || naReasonCategory;
    }
    onConfirm(selectedStatus, opts);
  }

  const statuses: CustomerRegisterItemStatus[] = [
    'todo', 'in_progress', 'blocked', 'review_needed', 'done', 'not_applicable', 'delegate_up',
  ];

  return (
    <dialog
      ref={dialogRef}
      onClose={onCancel}
      aria-label="Change status"
      style={{
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        color: 'var(--text)',
        padding: 'var(--space-4)',
        maxWidth: '400px',
        width: '90vw',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
          Change status
        </div>
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
          {itemTitle}
        </div>
        <fieldset
          style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}
          role="radiogroup"
          aria-label="Select new status"
        >
          {statuses.map((s) => (
            <label
              key={s}
              style={{
                display: 'flex',
                gap: 'var(--space-2)',
                alignItems: 'center',
                fontSize: 'var(--font-size-2xs)',
                cursor: 'pointer',
                padding: '4px 0',
              }}
            >
              <input
                type="radio"
                name="status"
                value={s}
                checked={selectedStatus === s}
                onChange={() => setSelectedStatus(s)}
              />
              <StatusBadge status={s} />
              <span>{statusLabel[s]}</span>
            </label>
          ))}
        </fieldset>

        {selectedStatus === 'blocked' ? (
          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
            <legend style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>
              Blocked reason (required)
            </legend>
            {BLOCKED_REASONS.map((r) => (
              <label key={r.value} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--font-size-2xs)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="blocked_reason"
                  value={r.value}
                  checked={blockedReason === r.value}
                  onChange={() => setBlockedReason(r.value)}
                />
                {r.label}
              </label>
            ))}
          </fieldset>
        ) : null}

        {selectedStatus === 'not_applicable' ? (
          <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
            <legend style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>
              N/A reason (required)
            </legend>
            {NA_REASONS.map((r) => (
              <label key={r.value} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--font-size-2xs)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="na_reason"
                  value={r.value}
                  checked={naReasonCategory === r.value}
                  onChange={() => setNaReasonCategory(r.value)}
                />
                {r.label}
              </label>
            ))}
            {needsRationale ? (
              <textarea
                value={naRationale}
                onChange={(e) => setNaRationale(e.target.value)}
                placeholder="Rationale or memo URL (required)"
                rows={3}
                required
                style={{
                  width: '100%',
                  padding: 'var(--space-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-input)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-2xs)',
                  fontFamily: 'var(--font-ui)',
                  resize: 'vertical',
                }}
              />
            ) : null}
          </fieldset>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              appearance: 'none',
              padding: '6px 12px',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-input)',
              background: 'var(--surface-alt)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <Button type="button" disabled={saving || !canConfirm} onClick={handleConfirm}>
            Save change
          </Button>
        </div>
      </div>
    </dialog>
  );
}

// ---- Bulk N/A dialog ---------------------------------------------------

interface BulkNaDialogProps {
  open: boolean;
  selectedCount: number;
  onConfirm: (naRationale: string) => void;
  onCancel: () => void;
  saving?: boolean;
}

export function BulkNaDialog({ open, selectedCount, onConfirm, onCancel, saving }: BulkNaDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [reasonCategory, setReasonCategory] = useState('geographic');
  const [rationale, setRationale] = useState('');

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (open) { setReasonCategory('geographic'); setRationale(''); }
  }, [open]);

  const needsRationale = ['superseded', 'risk_accepted', 'other'].includes(reasonCategory);
  const canConfirm = !needsRationale || rationale.trim().length > 0;

  return (
    <dialog
      ref={dialogRef}
      onClose={onCancel}
      aria-label="Mark items not applicable"
      style={{
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        color: 'var(--text)',
        padding: 'var(--space-4)',
        maxWidth: '420px',
        width: '90vw',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
          Mark {selectedCount} item{selectedCount !== 1 ? 's' : ''} N/A
        </div>
        <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
          <legend style={{ fontSize: 'var(--font-size-3xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>
            Reason (required)
          </legend>
          {NA_REASONS.map((r) => (
            <label key={r.value} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--font-size-2xs)', cursor: 'pointer' }}>
              <input
                type="radio"
                name="bulk_na_reason"
                value={r.value}
                checked={reasonCategory === r.value}
                onChange={() => setReasonCategory(r.value)}
              />
              {r.label}
            </label>
          ))}
          {needsRationale ? (
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Rationale or memo URL (required)"
              rows={3}
              required
              style={{
                width: '100%',
                padding: 'var(--space-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input)',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-2xs)',
                fontFamily: 'var(--font-ui)',
                resize: 'vertical',
              }}
            />
          ) : null}
        </fieldset>
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              appearance: 'none',
              padding: '6px 12px',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-input)',
              background: 'var(--surface-alt)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <Button type="button" disabled={saving || !canConfirm} onClick={() => onConfirm(rationale.trim() || reasonCategory)}>
            Confirm N/A
          </Button>
        </div>
      </div>
    </dialog>
  );
}
